import { useCallback, useEffect } from 'react';
import {
  AMPLITUDE_COOKIES,
  getAmplitudeCookieValue,
} from '@/utilities/logger/amplitude/amplitudeCookies';
import {
  AMP_EXP_STICKINESS_KEY,
  AMP_EXP_STICKINESS_TTL_MS,
  getUnifiedModule,
} from '@/utilities/logger/amplitude/initialise';
import { Logger } from '../../datadog/rumEvents/rumEvents';
import { getEnabledExperiments } from '../../datadog/telemetry-context';
import { DdEventCategory, DdEventType } from '../../datadog/types';
import _ from 'lodash';
import { setAmplitudeExperiments } from '@/store/actions';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { recordExperimentSource } from './experimentExposure';

export { AMPLITUDE_COOKIES };

function setStickinessVariants(variants: Record<string, unknown>): void {
  if (
    typeof localStorage === 'undefined' ||
    !variants ||
    Object.keys(variants).length === 0
  )
    return;
  try {
    // Stamp an absolute expiry so the reader can age the entry out (localStorage
    // has no native TTL). A fresh write always re-stamps, replacing older values.
    const envelope = {
      variants,
      expiresAt: Date.now() + AMP_EXP_STICKINESS_TTL_MS,
    };
    localStorage.setItem(AMP_EXP_STICKINESS_KEY, JSON.stringify(envelope));
  } catch {
    // ignore storage write errors (e.g. quota exceeded, blocked storage)
  }
}

const useAmplitudeExperiments = () => {
  const { userId, region } = useStore(useShallow((s) => s.userSessionContext));
  const amplitudeExperiments = useStore(
    useShallow((s) => s.amplitudeExperiments),
  );

  const fetchAndValidateExperiments = useCallback(async () => {
    try {
      const mod = await getUnifiedModule();
      const exp = mod.experiment();
      if (!exp || !userId) return;

      const user = {
        user_id: userId,
        user_properties: { region: region as string },
        device_id: getAmplitudeCookieValue(AMPLITUDE_COOKIES.AMP_DEVICE_ID),
      };

      // 1. Start client (loads flag configs for local evaluation; fetchOnStart: false so no fetch yet)
      await exp.start(user);
      // 2. Initial evaluation: use local evaluation only (flags + cookie/storage)
      const initialVariants = exp.all();

      if (initialVariants && Object.keys(initialVariants).length > 0) {
        recordExperimentSource(initialVariants, 'sdk');
        setAmplitudeExperiments({ ...initialVariants });

        Logger.logRumAction({
          name: '[GW][AMPLITUDE] Experiments local evaluation success',
          useCase: DdEventCategory.Initialization,
          eventType: DdEventType.Success,
          context: {
            experimentCount: Object.keys(initialVariants).length,
            enabledExperiments: getEnabledExperiments(initialVariants),
          },
        });
      }

      // 3. Fetch from Amplitude (remote)
      await exp.fetch(user, {});
      const variants = exp.all();

      // 4. Only replace experiments if remote evaluation is different from local
      if (
        variants &&
        Object.keys(variants).length > 0 &&
        !_.isEqual(variants, initialVariants)
      ) {
        recordExperimentSource(variants, 'sdk');
        setAmplitudeExperiments({ ...variants });
        setStickinessVariants(variants as Record<string, unknown>);

        Logger.logRumAction({
          name: '[GW][AMPLITUDE] Experiments remote evaluation success',
          useCase: DdEventCategory.Initialization,
          eventType: DdEventType.Success,
          context: {
            experimentCount: Object.keys(variants).length,
            enabledExperiments: getEnabledExperiments(variants),
          },
        });
      }
    } catch (err: unknown) {
      Logger.logRumError({
        name: '[GW][AMPLITUDE] Experiments initialization failed',
        useCase: DdEventCategory.Initialization,
        eventType: DdEventType.Error,
        context: {
          errorMessage: err instanceof Error ? err.message : String(err),
          experimentCount: Object.keys(amplitudeExperiments ?? {}).length,
          enabledExperiments: getEnabledExperiments(amplitudeExperiments),
        },
      });
    }
    /* amplitudeExperiments intentionally omitted: including it retriggers fetch in a loop. */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [userId, region]);

  useEffect(() => {
    fetchAndValidateExperiments();
  }, [fetchAndValidateExperiments]);
};

export default useAmplitudeExperiments;
